/**
 * PATH: packages/ui/src/hooks/useLastRouteResume.ts
 * FILE_ID: UI-HOOK-LAST-ROUTE-RESUME-001
 * PURPOSE: Remember the last in-app route per tenant and, on a cold app
 *          boot that lands on the root path, resume to that screen.
 * WHY: Operator (2026-06-15): "I should land on last opened screen when I
 *      open and review it." The app always boots to `/` → Dashboard, so
 *      reopening the tab/PWA loses the board/job/email the operator was
 *      reviewing. This hook records every content route and redirects the
 *      first root render to the stored route — once per boot, so clicking
 *      Home/Dashboard afterwards still works normally.
 * FLOW:
 *   render → resolveResumeTarget(stored, currentPath) captured once
 *          → effect: navigate(target, {replace}) if any
 *   location change → shouldRecordRoute(path) → writeLastRoute(scope, path)
 * DEPENDENCIES:
 * - react-router-dom: useLocation / useNavigate (already a @quotech/ui dep
 *   via useTabNavigation).
 * RELATED:
 * - apps/<tenant>/src/shell/AppShell.tsx: mounts this once at shell root.
 */

import { useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

const KEY_PREFIX = 'quotech:last-route:'

/** Paths we must never resume to or record (auth handshakes, onboarding). */
const TRANSIENT_PREFIXES = ['/auth', '/onboarding', '/logout', '/sso']

/**
 * Per-tenant storage scope. On dashboard.quotech.ai every tenant shares one
 * localStorage origin under `/company/<slug>/...`, so we key by the company
 * slug to stop one tenant's last route bleeding into another. Single-tenant
 * subdomains (path is just `/jobs`) fall back to `default`.
 *
 * NOTE: pass the FULL `window.location.pathname` here — the router's
 * `useLocation().pathname` strips the `/company/<slug>` basename, so it
 * would always resolve to `default`.
 */
export function tenantScopeFromPath(pathname: string): string {
  const segments = pathname.split('/').filter(Boolean)
  if (segments[0] === 'company' && segments[1]) return segments[1]
  return 'default'
}

/** Scope from the live full URL (includes the basename the router strips). */
function currentTenantScope(): string {
  if (typeof window === 'undefined') return 'default'
  return tenantScopeFromPath(window.location.pathname)
}

export function lastRouteKey(scope: string): string {
  return `${KEY_PREFIX}${scope}`
}

/** A route is recordable unless it's the transient auth/onboarding family. */
export function shouldRecordRoute(pathname: string): boolean {
  return !TRANSIENT_PREFIXES.some(
    (p) => pathname === p || pathname.startsWith(`${p}/`),
  )
}

/**
 * Pure resume decision. Returns the path to redirect to, or null.
 * Resume only when the cold boot landed on the root path AND a different,
 * non-transient route was stored. Never resume to root or a transient path.
 */
export function resolveResumeTarget(
  stored: string | null | undefined,
  currentPath: string,
): string | null {
  if (currentPath !== '/') return null
  if (!stored) return null
  if (stored === '/' || stored === '') return null
  if (!shouldRecordRoute(stored)) return null
  if (stored === currentPath) return null
  return stored
}

function readLastRoute(scope: string): string | null {
  try {
    return window.localStorage.getItem(lastRouteKey(scope))
  } catch {
    return null
  }
}

function writeLastRoute(scope: string, path: string): void {
  try {
    window.localStorage.setItem(lastRouteKey(scope), path)
  } catch {
    /* quota / disabled storage — fail soft */
  }
}

// Boot-scoped guard: the resume redirect must fire at most once per app
// load, otherwise clicking Home (→ `/`) would bounce the operator back to
// their last deep route and they could never reach the Dashboard.
let g_resumeConsumed = false

/** Test-only reset of the module boot guard. */
export function __resetLastRouteResumeForTests(): void {
  g_resumeConsumed = false
}

export interface UseLastRouteResumeOptions {
  /** Master switch (default true). */
  enabled?: boolean
}

export function useLastRouteResume(options: UseLastRouteResumeOptions = {}): void {
  const { enabled = true } = options
  const navigate = useNavigate()
  const location = useLocation()

  // Capture the resume target during the FIRST render — before the
  // recording effect can overwrite stored with the current path — so the
  // decision is based on the value persisted by the previous session.
  const targetRef = useRef<string | null | undefined>(undefined)
  if (targetRef.current === undefined) {
    if (!enabled || g_resumeConsumed || typeof window === 'undefined') {
      targetRef.current = null
    } else {
      const scope = currentTenantScope()
      targetRef.current = resolveResumeTarget(readLastRoute(scope), location.pathname)
    }
  }

  useEffect(() => {
    if (!enabled) return
    if (g_resumeConsumed) return
    g_resumeConsumed = true
    const target = targetRef.current
    if (target) navigate(target, { replace: true })
    // Run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return
    const path = `${location.pathname}${location.search}`
    if (!shouldRecordRoute(location.pathname)) return
    writeLastRoute(currentTenantScope(), path)
  }, [enabled, location.pathname, location.search])
}

export default useLastRouteResume
